// mds-dojo — dojo/on. Event listening + emit.
//
// dojo/on(target, type, listener):
//   - if target has its own on() method and isn't a DOM node → delegate to it
//     (this is how dojo/on works on Evented widgets, used by _WidgetsInTemplateMixin's
//      don(instance, event, fn) path).
//   - else attach a DOM listener (used by widgets: on(this.inputNode, 'input', fn)).
//
// on.emit(target, type, event): dispatch a DOM event, or (non-DOM) invoke on<type>.

// DEV-only: record runtime DOM listeners so an HMR tag-swap can re-attach them (tree-shaken from
// prod — every use is behind `import.meta.env.DEV`; the module is also glob-excluded).
import { recordListener, forgetListener } from './_base/hmr-listeners';

type Handle = { remove: () => void };

function domOn(target: any, type: string, listener: Function | ((e: Event) => any)): Handle {
  // Support comma-separated types (dojo allows "click,keyup").
  if (type.indexOf(',') !== -1) {
    const handles = type.split(/\s*,\s*/).map((t) => domOn(target, t, listener));
    return { remove() { handles.forEach((h) => h.remove()); } };
  }
  const evName = type.replace(/^on/, '').toLowerCase();
  target.addEventListener(evName, listener, false);
  if (import.meta.env.DEV && target && target.nodeType === 1) { recordListener(target, evName, listener, false); }
  return { remove() {
    target.removeEventListener(evName, listener, false);
    if (import.meta.env.DEV && target && target.nodeType === 1) { forgetListener(target, evName, listener, false); }
  } };
}

function on(target: any, type: any, listener: Function | ((e: any) => any)): Handle {
  // Extension events: real dojo/on, when `type` is a function (an "extension
  // event" like mouse.enter / touch.press / dijit/a11yclick / on.selector(...)),
  // dispatches by calling type(target, listener) and returns its handle. mds's
  // synthetic events (dojo/mouse, dojo/touch, dijit/a11yclick) are exactly such
  // (node, listener) => handle functions, and dijit/_AttachMixin maps template
  // events like mouseenter/mouseleave to them — so this branch is REQUIRED for
  // faithful dijit templates (e.g. dijit/Tooltip.html). Additive: previously a
  // function `type` fell through to domOn() and threw. (Added for the
  // Dialog/Tooltip family port.)
  if (typeof type === 'function') {
    return type(target, listener);
  }
  if (target && typeof target.on === 'function' && !target.nodeType) {
    return target.on(type, listener);
  }
  return domOn(target, type as string, listener);
}

// on.emit
(on as any).emit = function emit(target: any, type: string, event?: any): any {
  if (target && target.nodeType) {
    const evName = type.toLowerCase();
    let evt: Event;
    try {
      evt = new CustomEvent(evName, {
        bubbles: event && event.bubbles !== undefined ? event.bubbles : true,
        cancelable: event && event.cancelable !== undefined ? event.cancelable : true,
        detail: event && event.detail,
      });
    } catch (e) {
      evt = document.createEvent('HTMLEvents');
      (evt as any).initEvent(evName, true, true);
    }
    if (event) { for (const k in event) { try { (evt as any)[k] = event[k]; } catch (_e) { /* readonly */ } } }
    target.dispatchEvent(evt);
    return evt;
  }
  // non-DOM object: call on<type> method if present
  const method = 'on' + type;
  if (target && typeof target[method] === 'function') {
    return target[method].call(target, event);
  }
  return undefined;
};

// on.parse — used by dojo/Evented.on to route to an "add" callback per type.
(on as any).parse = function parse(target: any, type: string, listener: Function, addAdvice: (t: any, ty: string) => Handle): Handle {
  if (typeof type === 'string' && type.indexOf(',') !== -1) {
    const handles = type.split(/\s*,\s*/).map((t) => addAdvice(target, t));
    return { remove() { handles.forEach((h) => h.remove()); } };
  }
  return addAdvice(target, type);
};

export default on;
